import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PageTopBar from "../../../components/common/PageTopBar";
import { fieldHintTooltipProps, toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import { useNotify } from "../../../context/NotificationContext";
import { fetchAcademicYears } from "../../academicOperations/services/academicYearsService";
import { fetchGrades } from "../../academicOperations/services/gradesService";
import { AcademicYear, AcademicYearStatus, Grade } from "../../academicOperations/types";
import { fetchFeeStructures, saveFeeStructure } from "../services/feeStructureService";

const TERMS_PER_YEAR = 4;
const GRADUATE = "Graduate";

type SavedFee = { fee: number; locked: boolean; hasDues: boolean };
type RowState = "locked" | "saved" | "notSet" | "prefilled" | "edited";

const ROW_STATE_CHIP: Record<RowState, { label: string; color: "default" | "success" | "warning" | "info" | "primary" }> = {
  locked: { label: "Locked", color: "default" },
  saved: { label: "Saved", color: "success" },
  notSet: { label: "Not set", color: "warning" },
  prefilled: { label: "Prefilled", color: "info" },
  edited: { label: "Unsaved", color: "primary" },
};

const YEAR_STATUS_COLOR: Record<string, "success" | "info" | "default" | "warning"> = {
  [AcademicYearStatus.ACTIVE]: "success",
  [AcademicYearStatus.PLANNED]: "info",
  [AcademicYearStatus.INITIALIZED]: "warning",
  [AcademicYearStatus.CLOSED]: "default",
};

const formatAmount = (amount: number) => amount.toLocaleString("en-IN");

/**
 * Finance → Fee Structures: one term fee per grade for the selected academic year; each of the
 * 4 terms is charged it. Every year, CLOSED ones included, is edited here. A grade is locked only in
 * a CLOSED year once it has fee dues. Other years stay editable even with dues (e.g. a mid-year fee
 * change): existing dues keep the old fee, so saving such a change asks for confirmation first.
 */
const FeeStructuresPage: React.FC = () => {
  const notify = useNotify();
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [saved, setSaved] = useState<Record<number, SavedFee>>({}); // gradeId -> fee as stored
  const [edits, setEdits] = useState<Record<number, string>>({}); // gradeId -> fee as typed
  const [prefilledIds, setPrefilledIds] = useState<Set<number>>(new Set());
  const [pendingYear, setPendingYear] = useState<string | null>(null); // year switch waiting on discard confirm
  const [confirmDuesChange, setConfirmDuesChange] = useState(false); // save waiting on the "dues exist" confirm
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Academic years and grades load once; default to the ACTIVE year
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [years, allGrades] = await Promise.all([fetchAcademicYears(), fetchGrades()]);
        setAcademicYears(years);
        setGrades(
          allGrades
            .filter((g) => g.name !== GRADUATE)
            .sort((a, b) => (a.numericValue ?? Number.MAX_SAFE_INTEGER) - (b.numericValue ?? Number.MAX_SAFE_INTEGER))
        );
        const active = years.find((y) => y.status === AcademicYearStatus.ACTIVE);
        setSelectedYear(active?.name ?? years[0]?.name ?? "");
      } catch (err) {
        setError("Failed to load academic years and grades.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Fee structures reload whenever the year changes.
  // A PLANNED year's missing grades are prefilled (unsaved) from the ACTIVE year, else the latest CLOSED year.
  useEffect(() => {
    if (!selectedYear) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const structures = await fetchFeeStructures(selectedYear);
        const byGrade: Record<number, SavedFee> = {};
        structures.forEach((s) => (byGrade[s.gradeId] = { fee: s.fee, locked: Boolean(s.locked), hasDues: Boolean(s.hasDues) }));

        const prefill: Record<number, string> = {};
        const selected = academicYears.find((y) => y.name === selectedYear);
        if (selected?.status === AcademicYearStatus.PLANNED) {
          // List is newest first, so the first CLOSED year is the latest one
          const source =
            academicYears.find((y) => y.status === AcademicYearStatus.ACTIVE) ??
            academicYears.find((y) => y.status === AcademicYearStatus.CLOSED);
          if (source) {
            const sourceStructures = await fetchFeeStructures(source.name);
            sourceStructures
              .filter((s) => byGrade[s.gradeId] === undefined)
              .forEach((s) => (prefill[s.gradeId] = String(s.fee)));
          }
        }

        setSaved(byGrade);
        setEdits(prefill);
        setPrefilledIds(new Set(Object.keys(prefill).map(Number)));
      } catch (err) {
        setError("Failed to load fee structures.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedYear, academicYears]);

  const year = academicYears.find((y) => y.name === selectedYear);

  const feeText = (gradeId: number) =>
    edits[gradeId] ?? (saved[gradeId] !== undefined ? String(saved[gradeId].fee) : "");

  // Term fee must be a whole number greater than 0
  const feeError = (gradeId: number): string | null => {
    const text = edits[gradeId];
    if (text === undefined) return null;
    if (text.trim() === "") return "Required";
    if (!/^\d+$/.test(text.trim()) || Number(text) <= 0) return "Whole number greater than 0";
    return null;
  };

  const changedGradeIds = useMemo(
    () => Object.keys(edits).map(Number).filter((id) => edits[id].trim() !== String(saved[id]?.fee ?? "")),
    [edits, saved]
  );
  const hasErrors = changedGradeIds.some((id) => feeError(id) !== null);
  const setCount = grades.filter((g) => saved[g.id] !== undefined).length;

  const rowState = (gradeId: number): RowState => {
    if (saved[gradeId]?.locked) return "locked";
    if (changedGradeIds.includes(gradeId)) return prefilledIds.has(gradeId) && edits[gradeId] !== undefined ? "prefilled" : "edited";
    if (saved[gradeId] !== undefined) return "saved";
    return "notSet";
  };

  // Switching years with unsaved changes asks first
  const requestYearChange = (name: string) => {
    if (name === selectedYear) return;
    if (changedGradeIds.length > 0) setPendingYear(name);
    else setSelectedYear(name);
  };

  const handleDiscard = () => {
    setEdits({});
    setPrefilledIds(new Set());
  };

  // Changed grades whose fee already has dues this year (not locked, so the change is allowed)
  const changedWithDues = grades.filter((g) => changedGradeIds.includes(g.id) && saved[g.id]?.hasDues);

  // Changing a fee that already has dues asks first: those dues keep the old fee
  const requestSave = () => {
    if (changedWithDues.length > 0) setConfirmDuesChange(true);
    else handleSave();
  };

  const handleSave = async () => {
    setConfirmDuesChange(false);
    setSaving(true);
    try {
      for (const gradeId of changedGradeIds) {
        const result = await saveFeeStructure({
          gradeId,
          academicYearName: selectedYear,
          fee: Number(edits[gradeId].trim()),
        });
        setSaved((prev) => ({ ...prev, [gradeId]: { fee: result.fee, locked: Boolean(result.locked), hasDues: Boolean(result.hasDues) } }));
      }
      handleDiscard();
      notify(`Fee structures saved for ${selectedYear}.`);
    } catch (err: any) {
      // Backend sends the exception message as a plain string body
      const data = err?.response?.data;
      const message = typeof data === "string" ? data : data?.message;
      notify(message || "Failed to save fee structures.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageTopBar
        left="Fee Structures"
        right={
          <Box display="flex" alignItems="center" gap={1} sx={toolbarFieldsSx}>
            <TextField
              select
              size="small"
              label="Academic Year"
              value={selectedYear}
              onChange={(e) => requestYearChange(e.target.value)}
              sx={{ minWidth: 190 }}
              disabled={saving}
              SelectProps={{ renderValue: (value) => value as string }}
            >
              {academicYears.map((y) => (
                <MenuItem key={y.id} value={y.name} sx={{ fontSize: 14, justifyContent: "space-between", gap: 2 }}>
                  {y.name}
                  <Chip label={y.status} size="small" color={YEAR_STATUS_COLOR[y.status] ?? "default"} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                </MenuItem>
              ))}
            </TextField>
            {changedGradeIds.length > 0 && (
              <Button variant="text" size="small" sx={toolbarButtonSx} onClick={handleDiscard} disabled={saving}>
                Discard
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              sx={toolbarButtonSx}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={requestSave}
              disabled={saving || changedGradeIds.length === 0 || hasErrors}
            >
              {saving ? "Saving..." : changedGradeIds.length > 0 ? `Save (${changedGradeIds.length})` : "Save"}
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary strip: year status and how many grades have a fee */}
      {year && !loading && grades.length > 0 && (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
          <Chip label={year.status} size="small" color={YEAR_STATUS_COLOR[year.status] ?? "default"} />
          <Typography variant="body2" color="text.secondary">
            {year.startDate} to {year.endDate} · {setCount} of {grades.length} grades set
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : grades.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          No grades yet. Add grades under Academics → Setup.
        </Typography>
      ) : (
        // One compact row per grade
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ "& td, & th": { py: 0.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Term Fee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Year Total ({TERMS_PER_YEAR} terms)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grades.map((grade) => {
                const text = feeText(grade.id);
                const rowError = feeError(grade.id);
                const state = rowState(grade.id);
                const total = !rowError && text.trim() !== "" ? Number(text) * TERMS_PER_YEAR : null;
                return (
                  <TableRow key={grade.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{grade.name}</TableCell>
                    <TableCell>
                      {state === "locked" ? (
                        <Tooltip title="Fee dues exist for this grade in a closed year, so its fee can't be changed." {...fieldHintTooltipProps}>
                          <Box display="inline-flex" alignItems="center" gap={0.5}>
                            {formatAmount(saved[grade.id].fee)}
                            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <TextField
                          size="small"
                          value={text}
                          placeholder="Not set"
                          onChange={(e) => setEdits((prev) => ({ ...prev, [grade.id]: e.target.value }))}
                          error={rowError !== null}
                          title={rowError ?? undefined}
                          inputProps={{ inputMode: "numeric", style: { padding: "4px 8px", fontSize: 14 } }}
                          sx={{ width: 120 }}
                          disabled={saving}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: total !== null ? "text.primary" : "text.disabled" }}>
                      {total !== null ? formatAmount(total) : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ROW_STATE_CHIP[state].label}
                        size="small"
                        color={ROW_STATE_CHIP[state].color}
                        variant={state === "saved" || state === "locked" ? "outlined" : "filled"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasErrors && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
          Term fee must be a whole number greater than 0.
        </Typography>
      )}

      {/* Changing a fee that already has dues: existing dues keep the old fee */}
      <Dialog open={confirmDuesChange} onClose={() => setConfirmDuesChange(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change a fee that already has dues?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Fee dues already exist in {selectedYear} for{" "}
            <b>{changedWithDues.map((g) => `Grade ${g.name}`).join(", ")}</b>.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { typography: "body2", mb: 0.5 } }}>
            <li>Existing dues stay at the old fee. They are not changed.</li>
            <li>Only students activated or imported after this change get the new fee.</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDuesChange(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save new fee
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switching years with unsaved changes */}
      <Dialog open={pendingYear !== null} onClose={() => setPendingYear(null)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have {changedGradeIds.length} unsaved fee {changedGradeIds.length === 1 ? "change" : "changes"} for {selectedYear}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingYear(null)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              handleDiscard();
              setSelectedYear(pendingYear as string);
              setPendingYear(null);
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeStructuresPage;
